import { apiService } from '@shared/services/apiServices';
import contactRequestEndpoints from './contactRequestEndpoints';

/** Single entry returned by the batch presigned-URL endpoint. */
interface PresignedUrlFile {
  /** The placeholder path sent in the request. */
  originalPath: string;
  /** Final resolved S3 key (`contact-requests/{entityId}/attachments/{resourceId}/{filename}`). */
  path: string;
  /** Presigned PUT URL for uploading the file bytes. */
  url: string;
  /** Media resource UUID generated by the backend. */
  resourceId?: string;
}

export interface ContactRequestPresignedUrlsResult {
  success: boolean;
  data?: { files: PresignedUrlFile[] };
  error?: string;
}

/**
 * Generates presigned S3 upload URLs for contact-request completion attachments
 * (RR-1097).
 *
 * Placeholder paths are sent with `generatePath: true` so the backend fills in
 * the real `entityId`/`resourceId` segments and returns the resolved `path`,
 * upload `url`, and `resourceId` for each file. Abandoned uploads (tab close /
 * navigate away before submit) are expired by the S3 lifecycle rule; a
 * successful completion PATCH verifies + protects the uploaded objects.
 *
 * @param paths - One placeholder key per file, in the format
 *   `contact-requests/entityid-placeholder/attachments/resourceid-placeholder/{filename}`.
 */
export const generateContactRequestAttachmentPresignedUrls = async (
  paths: string[]
): Promise<ContactRequestPresignedUrlsResult> => {
  try {
    const result = await apiService.postAPI(
      contactRequestEndpoints.PRESIGNED_URLS_BATCH,
      { paths, generatePath: true }
    );
    if (result.error || result.isNetworkError) {
      return { success: false };
    }
    return {
      success: true,
      data: result.data as { files: PresignedUrlFile[] },
    };
  } catch {
    return { success: false };
  }
};
